/**
 * Helpers do modal de aprovação de médico (admin).
 * Normaliza payload legado/moderno e monta checklist de revisão.
 */

export type AdminReviewDocument = {
  id?: string;
  type?: string | null;
  documentType?: string | null;
  originalFileName?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  storageProvider?: string | null;
  hasFile?: boolean | null;
  fileUrl?: string | null;
  status?: string | null;
  rejectionReason?: string | null;
  createdAt?: string | Date | null;
  uploadedAt?: string | Date | null;
  certificateKind?: string | null;
};

export type AdminReviewDoctor = {
  crm?: string | null;
  crmState?: string | null;
  specialty?: string | null;
  professionalDocumentsUrl?: string | null;
  user?: { name?: string | null; email?: string | null } | null;
  documents?: AdminReviewDocument[] | null;
};

export const DOC_TYPE_LABELS: Record<string, string> = {
  CRM: 'CRM (arquivo)',
  SPECIALTY_CERTIFICATE: 'Certificado de Especialidade',
  INSCRIPTION_PROOF: 'Comprovante de Inscrição',
  CONFIRMATION_VIDEO: 'Vídeo de confirmação',
  DIGITAL_CERTIFICATE: 'Certificado .pfx no Dr House (opcional)',
};

export function getDocumentType(doc: AdminReviewDocument): string {
  return String(doc.documentType || doc.type || '').trim();
}

export { formatDoctorCrmDisplay, hasPublicDoctorCrm, getDoctorCrmCardLabel } from './doctorCrm';

export function normalizeAdminReviewDocument(
  doc: AdminReviewDocument,
): AdminReviewDocument & { documentType: string; type: string } {
  const documentType = getDocumentType(doc) || 'UNKNOWN';
  return {
    ...doc,
    type: documentType,
    documentType,
    originalFileName: doc.originalFileName || doc.fileName || undefined,
    uploadedAt: doc.uploadedAt || doc.createdAt || undefined,
  };
}

export interface DoctorReviewChecklist {
  hasIdentityData: boolean;
  hasCrm: boolean;
  hasDocuments: boolean;
  hasSpecialtyCertificate?: boolean;
  hasInscriptionProof?: boolean;
  hasConfirmationVideo: boolean;
  hasDigitalCertificate: boolean;
  isLegacyDocumentsOnly: boolean;
  canApprove: boolean;
  approveBlockReason: string | null;
}

/**
 * Checklist de revisão no modal.
 * Fluxo novo: CRM + especialidade + inscrição + vídeo. .pfx no Dr House é informativo.
 * Assinatura de receita/atestado/exame é só na Memed (Bird ID). Legado: professionalDocumentsUrl.
 */
export function buildDoctorReviewChecklist(
  doctor: AdminReviewDoctor,
  documents: AdminReviewDocument[] = [],
): DoctorReviewChecklist {
  const normalized = documents.map(normalizeAdminReviewDocument);
  const fromDoctor = (doctor.documents || []).map(normalizeAdminReviewDocument);
  const allDocs = normalized.length > 0 ? normalized : fromDoctor;

  const hasIdentityData = !!(doctor.user?.name || doctor.user?.email || doctor.specialty);
  const hasCrm = !!(String(doctor.crm || '').trim() && String(doctor.crmState || '').trim());
  const hasConfirmationVideo = allDocs.some(
    (d) => getDocumentType(d) === 'CONFIRMATION_VIDEO',
  );
  const hasDigitalCertificate = allDocs.some(
    (d) => getDocumentType(d) === 'DIGITAL_CERTIFICATE',
  );
  const hasModernCrm = allDocs.some((d) => getDocumentType(d) === 'CRM');
  const hasModernSpecialtyCertificate = allDocs.some((d) => getDocumentType(d) === 'SPECIALTY_CERTIFICATE');
  const hasModernInscriptionProof = allDocs.some((d) => getDocumentType(d) === 'INSCRIPTION_PROOF');
  const hasModernDocs = allDocs.length > 0;
  const hasLegacyDocuments = !!doctor.professionalDocumentsUrl;
  const hasDocuments = hasModernDocs || hasLegacyDocuments;
  const isLegacyDocumentsOnly = !hasModernDocs && hasLegacyDocuments;

  let canApprove = false;
  let approveBlockReason: string | null = null;

  if (!hasDocuments) {
    approveBlockReason =
      'Este médico ainda não enviou documentos profissionais.';
  } else if (
    hasModernDocs &&
    (!hasConfirmationVideo ||
      !hasModernCrm ||
      !hasModernSpecialtyCertificate ||
      !hasModernInscriptionProof)
  ) {
    approveBlockReason =
      'É necessário enviar os 4 documentos obrigatórios (CRM, Especialidade, Inscrição e Vídeo) para aprovar. Assinatura digital é na Memed (Bird ID), não no cadastro Dr House.';
  } else {
    canApprove = true;
  }

  return {
    hasIdentityData,
    hasCrm,
    hasDocuments,
    hasSpecialtyCertificate: hasModernSpecialtyCertificate,
    hasInscriptionProof: hasModernInscriptionProof,
    hasConfirmationVideo,
    hasDigitalCertificate,
    isLegacyDocumentsOnly,
    canApprove,
    approveBlockReason,
  };
}

export function partitionReviewDocuments(documents: AdminReviewDocument[]): {
  papers: AdminReviewDocument[];
  videos: AdminReviewDocument[];
} {
  const papers: AdminReviewDocument[] = [];
  const videos: AdminReviewDocument[] = [];
  for (const doc of documents.map(normalizeAdminReviewDocument)) {
    if (getDocumentType(doc) === 'CONFIRMATION_VIDEO') {
      videos.push(doc);
    } else {
      papers.push(doc);
    }
  }
  return { papers, videos };
}
